import { useEpdLcaCustomResultGridData } from 'services/api/queries';

import {
  createVariationColumnWithDisclaimer,
  LcaResultsAdditionalGridColumns,
  LcaResultsGridColumnsPdf,
} from '../LcaResultsGridColumns';
import LcaResultsGridPdf from '../LcaResultsGridPdf';
import LcaCustomResultsDynamicGrid from './LcaCustomResultsDynamicGrid';
import { LcaCustomResultModel } from 'types/types';
import { LcaGridNote } from '../../GridService';
import useLcaGridHelper from 'components/v2/compiler/hooks/useLcaGridHelper';

type TProps = {
  epdVersionId: string;
  customResult: LcaCustomResultModel;
  isForPdf?: boolean;
  disabled?: boolean;
  preferEnabledAppearanceEvenWhenDisabled?: boolean;
  onChangedCustomResult?: (propertyName: string, val: any) => void;
  className?: string;
};

const LcaCustomAdditionalResultsGrid = ({
  epdVersionId,
  customResult,
  isForPdf,
  disabled,
  preferEnabledAppearanceEvenWhenDisabled,
  className,
  onChangedCustomResult,
}: TProps) => {
  const rows = useEpdLcaCustomResultGridData(customResult.id).data;

  const { generalDisclaimerNote, showVariationColumn, createVariationDisclaimerNote } = useLcaGridHelper(epdVersionId);

  const notes: LcaGridNote[] = [];

  if (generalDisclaimerNote) {
    notes.push(generalDisclaimerNote);
  }

  let columns = isForPdf ? LcaResultsGridColumnsPdf : LcaResultsAdditionalGridColumns;
  if (showVariationColumn && (!isForPdf || (isForPdf && rows?.some((r) => r.variation)))) {
    const disclaimerNumber = 1;
    columns = [...columns, createVariationColumnWithDisclaimer(disclaimerNumber)];
    notes.push(createVariationDisclaimerNote(disclaimerNumber));
  }

  const editableFields: { fieldName: string; label: React.ReactNode; value: string; placeholder: string }[] = [
    {
      fieldName: 'gridAcronyms',
      label: 'Acronyms',
      value: customResult.gridAcronyms,
      placeholder: 'Acronym 1 description; Acronym 2 - description; etc.',
    },
    {
      fieldName: 'gridDisclaimers',
      label: <span>Disclaimers</span>,
      value: customResult.gridDisclaimers,
      placeholder:
        notes.length === 0
          ? 'Disclaimer 1 description; Disclaimer 2 - description; etc.'
          : 'Disclaimer 2 description; Disclaimer 3 - description; etc.',
    },
  ];

  if (isForPdf) {
    return <LcaResultsGridPdf rows={rows} columns={columns} notes={notes} editableFields={editableFields} />;
  }

  return (
    <LcaCustomResultsDynamicGrid
      className={className}
      title="Additional environmental performance results"
      epdVersionId={epdVersionId}
      customResultId={customResult.id}
      rows={rows}
      columns={columns}
      editableFields={editableFields}
      notes={notes}
      onChangedCustomResult={onChangedCustomResult}
      disabled={disabled}
      preferEnabledAppearanceEvenWhenDisabled={preferEnabledAppearanceEvenWhenDisabled}
    />
  );
};

export default LcaCustomAdditionalResultsGrid;
